/**
 * §11 — the rules the kids desk works to, as functions rather than as JSX.
 *
 * Two questions live here, and they are the two the attendant is asked while a
 * parent watches:
 *
 *  1. **May this adult take this child?** `kids-area.service.ts` answers it
 *     server-side and refuses anything else, so nothing below is a security
 *     boundary. It is the *desk's* boundary: the screen has to reach the
 *     server's verdict before it posts, or a handover becomes a 403 in front of
 *     a queue. Expressed as one function so it can be asserted rather than
 *     clicked.
 *  2. **Whose time is nearly up?** The board is ordered by it, because that is
 *     the order the desk actually works in.
 */
import { billableSeconds } from '@/lib/domain';
import type { SessionRow } from './lib/sessions';
import { guardianIdsOf, type ChildRow } from './components/kids-area';

/** `KidsCheckOutDto.overrideReason` is `@MinLength(3)`; a shorter one is a 400. */
export const MIN_OVERRIDE_REASON_LENGTH = 3;

/**
 * How the collection in front of the desk stands.
 *
 * - `unidentified` — no band scanned, no child picked. Nothing to release.
 * - `unresolved` — a band that matches nothing on screen. The post carries the
 *   tag and no collector, and the server does the naming: it accepts the single
 *   guardian on file and refuses with 400 when the child has none, or has two
 *   and nobody said which turned up. The one place naming nobody is safe,
 *   because the only case it releases is the one with no ambiguity to resolve.
 * - `unnamed` — the child is known and nobody has said who is taking them.
 * - `guardian` — an adult ON THE CHILD'S RECORD. The arrangement the venue
 *   accepted at check-in; nothing to sign. Note the plural: `ChildGuardian` is
 *   many-to-many because a child usually has two, so the second parent is no
 *   longer a stranger who needs a signature to collect their own child. The
 *   safety property is untouched — someone who is not on the record still
 *   cannot take the child without a manager signing — and this is the one
 *   change of substance the move off `hostParticipantId` made.
 * - `override` — anyone else, including every candidate when no guardian was
 *   ever recorded.
 */
export type CollectionState =
  | 'unidentified'
  | 'unresolved'
  | 'unnamed'
  | 'guardian'
  | 'override';

export interface CollectionFacts {
  /** A band has been typed, or a child picked from the area. */
  identified: boolean;
  /** The guardians on the child's record, when the child was recognised. */
  child: { guardianIds: string[] } | null;
  /** The `Guardian` the desk named as collecting. */
  collectorId: string;
}

export function collectionState(facts: CollectionFacts): CollectionState {
  if (!facts.identified) return 'unidentified';
  if (!facts.child) return 'unresolved';
  if (!facts.collectorId) return 'unnamed';
  return facts.child.guardianIds.includes(facts.collectorId) ? 'guardian' : 'override';
}

export interface OverrideSignature {
  managerPin: string;
  overrideReason: string;
}

export interface ReleaseGates {
  /** The ordinary check-out. Never true on the override path. */
  release: boolean;
  /** The manager's signature, complete. */
  override: boolean;
  /** Whether to put the signature block in front of the desk at all. */
  needsSignature: boolean;
}

/**
 * The two buttons, decided separately and never both live.
 *
 * The override is deliberately *not* the same button with extra fields: it is a
 * second, differently-worded, differently-coloured act, so releasing a child to
 * someone other than their guardian cannot happen through muscle memory.
 */
export function releaseGates(
  state: CollectionState,
  signature: OverrideSignature,
): ReleaseGates {
  if (state === 'override') {
    const reason = signature.overrideReason.trim();
    return {
      release: false,
      override:
        signature.managerPin.trim().length > 0 && reason.length >= MIN_OVERRIDE_REASON_LENGTH,
      needsSignature: true,
    };
  }
  return {
    release: state === 'guardian' || state === 'unresolved',
    override: false,
    needsSignature: false,
  };
}

// ---------------------------------------------------------------------------
// The board
// ---------------------------------------------------------------------------

/** The first mark `sweepExpiring()` warns at, so the board turns amber with it. */
export const NEARLY_UP_SECONDS = 10 * 60;

export interface Occupant {
  session: SessionRow;
  /** The child's own record, when this terminal may read one. */
  child: ChildRow | null;
  childName: string | null;
  tagCode: string | null;
  /**
   * Every adult on this child's record. Empty means the handover will need a
   * manager, which is why the board says so before anyone comes to collect.
   */
  guardianIds: string[];
  /**
   * Wall-clock seconds since check-in.
   *
   * Deliberately not the billing meter. A paused timer bills nothing, and the
   * child is still in the room: "how long has this one been here" is a
   * supervision question, and answering it with billable time would under-report
   * exactly the child who has been left longest.
   */
  dwellSeconds: number;
  /** Seconds of the booked window left; negative once it is spent, null if open-ended. */
  remainingSeconds: number | null;
  overdueSeconds: number;
  paused: boolean;
}

export function occupantOf(session: SessionRow, child: ChildRow | null, now: Date): Occupant {
  const startedAt = session.startedAt ? Date.parse(session.startedAt) : Number.NaN;
  const dwellSeconds = Number.isNaN(startedAt)
    ? session.accumulatedSeconds
    : Math.max(0, Math.round((now.getTime() - startedAt) / 1000));

  const remainingSeconds = session.autoCloseAt
    ? Math.round((Date.parse(session.autoCloseAt) - now.getTime()) / 1000)
    : session.plannedMinutes
      ? session.plannedMinutes * 60 - billableSeconds(session, now)
      : null;

  return {
    session,
    child,
    childName: child?.displayName ?? null,
    tagCode: child?.tagCode ?? null,
    guardianIds: guardianIdsOf(child),
    dwellSeconds,
    remainingSeconds,
    overdueSeconds: remainingSeconds !== null && remainingSeconds < 0 ? -remainingSeconds : 0,
    paused: session.status === 'PAUSED',
  };
}

export function urgencyOf(occupant: Occupant): 'over' | 'soon' | 'ok' {
  if (occupant.remainingSeconds === null) return 'ok';
  if (occupant.remainingSeconds < 0) return 'over';
  return occupant.remainingSeconds <= NEARLY_UP_SECONDS ? 'soon' : 'ok';
}

/**
 * The order the desk works in: whoever the attendant has to deal with next,
 * first. Over their time before nearly out of it, nearly out before comfortable,
 * and an open-ended stay last — it can never come due, so it can never be the
 * next thing to do. Longest here breaks the tie, because among children who
 * cannot run out of time the one who has been in longest is the one to look at.
 */
export function compareOccupants(a: Occupant, b: Occupant): number {
  const rank = (occupant: Occupant) => (occupant.remainingSeconds === null ? 1 : 0);
  if (rank(a) !== rank(b)) return rank(a) - rank(b);
  if (a.remainingSeconds !== null && b.remainingSeconds !== null) {
    if (a.remainingSeconds !== b.remainingSeconds) return a.remainingSeconds - b.remainingSeconds;
  }
  if (a.dwellSeconds !== b.dwellSeconds) return b.dwellSeconds - a.dwellSeconds;
  return a.session.id < b.session.id ? -1 : a.session.id > b.session.id ? 1 : 0;
}
